export const isDomainCapability = (item) =>
  item?.source === 'capability' && /^(domain|dns)\./.test(String(item?.id || ''));

const emptyValue = (schema) => {
  if (schema?.default !== undefined) return schema.default;
  if (schema?.example !== undefined) return schema.example;
  if (schema?.type === 'object') {
    return Object.fromEntries(
      Object.entries(schema.properties || {}).map(([key, value]) => [key, emptyValue(value)]),
    );
  }
  if (schema?.type === 'array') return [];
  // Optional booleans need an explicit unset state. Initializing every boolean
  // to false makes the schema workbench send fields the user never configured
  // (for example proxied=false on TXT and MX DNS records).
  if (schema?.type === 'boolean') return '';
  if (schema?.type === 'number' || schema?.type === 'integer') return '';
  return '';
};

export function schemaDraft(schema) {
  return Object.fromEntries(
    Object.entries(schema?.properties || {}).map(([key, property]) => {
      const value = emptyValue(property);
      return [
        key,
        property?.type === 'object' || property?.type === 'array'
          ? JSON.stringify(value, null, 2)
          : value,
      ];
    }),
  );
}

export function buildSchemaInput(schema, draft) {
  const result = {};
  const required = new Set(schema?.required || []);
  for (const [key, property] of Object.entries(schema?.properties || {})) {
    let value = draft?.[key];
    if (value === '' || value === undefined || value === null) {
      if (required.has(key)) return { error: `${key} is required` };
      continue;
    }
    if ((property?.type === 'object' || property?.type === 'array') && typeof value === 'string') {
      try { value = JSON.parse(value); }
      catch (error) { return { error: `${key}: ${error.message}` }; }
    }
    if (property?.type === 'number' || property?.type === 'integer') {
      value = Number(value);
      if (!Number.isFinite(value)) return { error: `${key} must be a number` };
      if (property.type === 'integer' && !Number.isInteger(value)) return { error: `${key} must be an integer` };
      const numericBranches = (property.anyOf || property.oneOf || []).filter((branch) => (
        branch?.type === 'number' || branch?.type === 'integer'
      ));
      const inRange = (branch) => (
        (branch.minimum === undefined || value >= branch.minimum) &&
        (branch.maximum === undefined || value <= branch.maximum)
      );
      if (numericBranches.length > 0 && !numericBranches.some(inRange)) return { error: `${key} is outside the allowed range` };
      if (property.minimum !== undefined && value < property.minimum) return { error: `${key} is outside the allowed range` };
      if (property.maximum !== undefined && value > property.maximum) return { error: `${key} is outside the allowed range` };
    }
    result[key] = value;
  }
  return { input: result, error: null };
}

export function unwrapActionResponse(response) {
  const body = response?.body;
  if (!response?.ok) {
    const message = body?.error?.message || body?.message || `Request failed (${response?.status || 0})`;
    throw new Error(String(message));
  }
  if (body?.success === false || body?.error) {
    throw new Error(String(body?.error?.message || body?.message || 'Action failed'));
  }
  return body?.success === true ? body.data : body;
}

export function normalizeDomainList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.domains)) return value.domains;
  return [];
}

export function normalizeDnsRecords(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.records)) return value.records;
  if (Array.isArray(value?.items)) return value.items;
  return [];
}

export function isDnsRecordManaged(record) {
  return !!(record?.managed_by_apps || record?.managed_by_argo_tunnel || record?.auto_added);
}

export function isDnsRecordReadOnly(record) {
  return !!record?.locked;
}

export function isDnsRecordTypeWritable(record, capabilities) {
  return capabilities?.record_types?.includes(String(record?.type || '').toUpperCase()) === true;
}

export function dnsRecordSupportsProxy(record, type, capabilities) {
  const normalizedType = String(type || '').toUpperCase();
  const typeSupportsProxy = !!capabilities?.proxied?.supported
    && capabilities.proxied.record_types.includes(normalizedType);
  if (!typeSupportsProxy) return false;
  const existingType = String(record?.type || '').toUpperCase();
  return existingType !== normalizedType || record?.proxiable !== false;
}

const CLOUDFLARE_DNS_CAPABILITIES = {
  registrar: 'cloudflare',
  record_types: ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'CAA', 'SRV'],
  record_fields: { MX: ['priority'], CAA: ['flags', 'tag'], SRV: ['priority', 'weight', 'port'] },
  ttl: { supported: true, min: 60, max: 86400, default: 1, automatic: 1 },
  proxied: { supported: true, record_types: ['A', 'AAAA', 'CNAME'], requires_automatic_ttl: true },
};

export function normalizeDnsCapabilities(value) {
  const fallback = CLOUDFLARE_DNS_CAPABILITIES;
  const supplied = value?.capabilities || {};
  const raw = supplied.registrar && String(supplied.registrar).toLowerCase() !== 'cloudflare' ? {} : supplied;
  return {
    ...fallback,
    ...raw,
    registrar: 'cloudflare',
    record_types: Array.isArray(raw.record_types)
      ? raw.record_types.map((type) => String(type).toUpperCase())
      : fallback.record_types,
    record_fields: { ...fallback.record_fields, ...(raw.record_fields || {}) },
    ttl: { ...fallback.ttl, ...(raw.ttl || {}) },
    proxied: {
      ...fallback.proxied,
      ...(raw.proxied || {}),
      record_types: Array.isArray(raw.proxied?.record_types)
        ? raw.proxied.record_types.map((type) => String(type).toUpperCase())
        : fallback.proxied.record_types,
    },
  };
}

export function hasCloudflareDnsCapabilities(value) {
  const capabilities = value?.capabilities;
  return String(capabilities?.registrar || '').toLowerCase() === 'cloudflare'
    && Array.isArray(capabilities?.record_types)
    && capabilities.record_types.length > 0
    && Number.isFinite(capabilities?.ttl?.min)
    && Number.isFinite(capabilities?.ttl?.max)
    && Array.isArray(capabilities?.proxied?.record_types);
}

export function isCloudflareDomain(domain) {
  return String(domain?.registrar || '').trim().toLowerCase() === 'cloudflare';
}

const isIpv4 = (value) => {
  const parts = String(value).split('.');
  return parts.length === 4 && parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
};

const isIpv6 = (value) => {
  const address = String(value).trim();
  if (!address.includes(':') || !/^[0-9a-f:.]+$/i.test(address)) return false;
  const compressed = address.split('::');
  if (compressed.length > 2) return false;
  const parts = compressed.flatMap((side) => side ? side.split(':') : []);
  if (parts.some((part) => part === '')) return false;
  const ipv4Index = parts.findIndex((part) => part.includes('.'));
  if (ipv4Index !== -1 && (ipv4Index !== parts.length - 1 || !isIpv4(parts[ipv4Index]))) return false;
  if (parts.some((part, index) => index !== ipv4Index && !/^[0-9a-f]{1,4}$/i.test(part))) return false;
  const units = parts.length + (ipv4Index === -1 ? 0 : 1);
  return compressed.length === 2 ? units < 8 : units === 8;
};

const isDnsRecordName = (value) => {
  if (value === '@') return true;
  const name = String(value).trim().replace(/\.$/, '');
  if (!name || name.length > 253) return false;
  return name.split('.').every((label, index) => {
    if (!label || label.length > 63) return false;
    if (label === '*') return index === 0;
    return /^[a-z0-9_](?:[a-z0-9_-]*[a-z0-9_])?$/i.test(label);
  });
};

const isDnsHostname = (value) => {
  const hostname = String(value).trim().replace(/\.$/, '');
  return hostname.length > 0 && hostname.length <= 253 && hostname.split('.').every((label) => (
    label.length > 0 && label.length <= 63 && /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label)
  ));
};

const normalizedDnsName = (value) => String(value || '@').trim().replace(/\.$/, '').toLowerCase();

export function validateDnsRecordSetInput(form, records = [], editingIndex = -1) {
  const type = String(form?.type || '').trim().toUpperCase();
  const name = normalizedDnsName(form?.subdomain);
  const peers = records.filter((record, index) => (
    index !== editingIndex
    && normalizedDnsName(record?.name ?? record?.subdomain) === name
    && String(record?.type || '').trim().toUpperCase() === type
  ));
  if (type === 'NS' && peers.length >= 10) return { error: 'dnsNsLimit' };
  if (type === 'TXT') {
    const combined = peers.reduce(
      (length, record) => length + String(record?.value ?? record?.content ?? '').length,
      String(form?.value || '').trim().length,
    );
    if (combined > 8192) return { error: 'dnsTxtSetTooLong' };
  }
  return { error: null };
}

export function hasMixedAddressProxyState(form, records = [], editingIndex = -1) {
  const type = String(form?.type || '').trim().toUpperCase();
  if (!['A', 'AAAA'].includes(type)) return false;
  const name = normalizedDnsName(form?.subdomain);
  return records.some((record, index) => (
    index !== editingIndex
    && ['A', 'AAAA'].includes(String(record?.type || '').trim().toUpperCase())
    && normalizedDnsName(record?.name ?? record?.subdomain) === name
    && record?.proxied !== undefined
    && Boolean(record.proxied) !== Boolean(form?.proxied)
  ));
}

export function hasNsDelegationShadowRisk(form, records = [], editingIndex = -1) {
  if (String(form?.type || '').trim().toUpperCase() !== 'NS') return false;
  const name = normalizedDnsName(form?.subdomain);
  const alreadyDelegated = records.some((record, index) => (
    index !== editingIndex
    && String(record?.type || '').trim().toUpperCase() === 'NS'
    && normalizedDnsName(record?.name ?? record?.subdomain) === name
  ));
  if (alreadyDelegated) return false;
  return records.some((record, index) => {
    if (index === editingIndex || String(record?.type || '').trim().toUpperCase() === 'NS') return false;
    const recordName = normalizedDnsName(record?.name ?? record?.subdomain);
    return name === '@' || recordName === name || recordName.endsWith(`.${name}`);
  });
}

export function dnssecPresentation(value) {
  const state = value && typeof value === 'object' ? value : { status: value };
  const status = state.provider_status ?? state.status;
  const desiredEnabled = typeof state.desired_enabled === 'boolean' ? state.desired_enabled : undefined;
  const effectiveEnabled = typeof state.effective_enabled === 'boolean' ? state.effective_enabled : undefined;
  const actionRequired = state.action_required === true;
  switch (String(status || '').toLowerCase()) {
    case 'active': return { label: 'dnssecActive', tone: 'ok', enabled: desiredEnabled ?? true, effective: effectiveEnabled ?? true, transitional: false, actionRequired };
    case 'pending': return { label: 'dnssecPending', tone: 'pending', enabled: desiredEnabled ?? true, effective: effectiveEnabled ?? false, transitional: true, actionRequired };
    case 'pending-disabled': return { label: 'dnssecPendingDisabled', tone: 'pending', enabled: desiredEnabled ?? false, effective: effectiveEnabled ?? true, transitional: true, actionRequired };
    case 'disabled': return { label: 'dnssecDisabled', tone: 'pending', enabled: desiredEnabled ?? false, effective: effectiveEnabled ?? false, transitional: false, actionRequired };
    case 'error': return { label: 'dnssecError', tone: 'bad', enabled: desiredEnabled ?? false, effective: effectiveEnabled, transitional: false, actionRequired: true };
    default: return { label: 'dnssecUnavailable', tone: 'bad', enabled: desiredEnabled ?? false, effective: effectiveEnabled, transitional: false, actionRequired };
  }
}

export function validateDnsRecordInput(form, capabilities) {
  const type = String(form?.type || '').trim().toUpperCase();
  const value = String(form?.value || '').trim();
  const name = String(form?.subdomain || '').trim();
  if (!type || !name || !value) return { error: 'dnsRequired' };
  if (!capabilities.record_types.includes(type)) return { error: 'dnsTypeUnsupported' };
  if (!isDnsRecordName(name)) return { error: 'dnsInvalidName' };
  if (type === 'A' && !isIpv4(value)) return { error: 'dnsInvalidIpv4' };
  if (type === 'AAAA' && !isIpv6(value)) return { error: 'dnsInvalidIpv6' };
  if (new TextEncoder().encode(value).length > 4096) return { error: 'dnsValueTooLong' };
  const permitsNullTarget = ['MX', 'SRV'].includes(type) && value === '.';
  if (['CNAME', 'MX', 'NS', 'SRV'].includes(type) && !isDnsHostname(value) && !permitsNullTarget) return { error: 'dnsInvalidHostname' };
  if (form.priority !== '' && form.priority !== undefined) {
    const priority = Number(form.priority);
    if (!Number.isInteger(priority) || priority < 0 || priority > 65535) return { error: 'dnsInvalidPriority' };
  }
  if (type === 'MX' && String(form.priority ?? '') === '') return { error: 'dnsInvalidPriority' };
  if (type === 'SRV') {
    for (const field of ['priority', 'weight', 'port']) {
      const numeric = Number(form[field]);
      if (String(form[field] ?? '') === '' || !Number.isInteger(numeric) || numeric < 0 || numeric > 65535) return { error: 'dnsInvalidSrv' };
    }
  }
  if (type === 'CAA') {
    const flags = Number(form.flags);
    if (String(form.flags ?? '') === '' || !Number.isInteger(flags) || flags < 0 || flags > 255 || !String(form.tag || '').trim()) return { error: 'dnsInvalidCaa' };
  }
  if (String(form.comment || '').length > 100) return { error: 'dnsCommentTooLong' };
  const tags = String(form.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
  if (tags.length > 20 || tags.some((tag) => {
    const separator = tag.indexOf(':');
    const name = separator < 0 ? tag : tag.slice(0, separator);
    const tagValue = separator < 0 ? '' : tag.slice(separator + 1);
    return separator <= 0 || !/^[A-Za-z0-9_-]{1,32}$/.test(name) || tagValue.length > 100 || /[\r\n]/.test(tagValue);
  })) return { error: 'dnsInvalidTags' };
  if (form.ttl !== '' && form.ttl !== undefined) {
    const ttl = Number(form.ttl);
    const allowedAutomatic = capabilities.ttl.automatic !== undefined && ttl === capabilities.ttl.automatic;
    if (!Number.isInteger(ttl) || (!allowedAutomatic && (ttl < capabilities.ttl.min || ttl > capabilities.ttl.max))) {
      return { error: 'dnsInvalidTtl' };
    }
  }
  if (form.proxied) {
    if (!capabilities.proxied.supported || !capabilities.proxied.record_types.includes(type)) return { error: 'dnsProxyUnsupported' };
    if (capabilities.proxied.requires_automatic_ttl && Number(form.ttl) !== capabilities.ttl.automatic) return { error: 'dnsProxyTtl' };
  }
  return { error: null };
}

export function displayDomainName(domain) {
  return domain?.domainName || domain?.domain_name || domain?.domain || 'Unknown domain';
}

export function formatExpiry(value, locale = 'en') {
  if (value == null || value === '') return '—';
  const n = Number(value);
  const date = Number.isFinite(n)
    ? new Date(n < 10_000_000_000 ? n * 1000 : n)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale === 'zh' ? 'zh-CN' : 'en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(date);
}

export function makeIdempotencyKey(prefix) {
  const uuid = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${uuid}`;
}
